import { Assessment as AssessmentIcon, ModeComment, Security as SecurityIcon } from '@mui/icons-material';
import { Box, CircularProgress, Grid, Tooltip } from '@mui/material';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import BotonListasConJugador from '../components/botones/BotonListasConJugador';
import { ContainerApp, PageHeaderBox } from '../components/custom/CustomComponents';
import CrearJugadorModal from '../components/jugador/CrearJugadorModal';
import DatosJugador from '../components/jugador/DatosJugador';
import HistoricoJugador from '../components/jugador/HistoricoJugador';
import InfoJugadorManual from '../components/jugador/InfoJugadorManual';
import ModalInformesJugador from '../components/jugador/ModalInformesJugador';
import ModalObservacionesJugador from '../components/jugador/ModalObservacionesJugador';
import TablaHistorialRendimiento from '../components/jugador/TablaHistorialRendimiento';
import { Context } from '../service/Context';
import theme from '../styles/theme';
import { capitalizar, extraerNombreClub } from '../utils/UtilesComun';
import { obtenerColorNota } from '../utils/UtilesJugador';

/**
 * Pagina de un jugador.
 */
const Jugador = () => {
  const {
    obtenerDatosJugador,
    obtenerHistoricoJugador,
    obtenerHistorialEvaluacionesJugador,
    obtenerObservacionesJugador,
    obtenerInformesJugador,
    eliminarJugador,
    temporadaActual,
    jugadoresCache,
    setJugadorCacheItem,
  } = useContext(Context);

  const { routeIdJugador } = useParams();
  const navigate = useNavigate();
  const editModalRef = useRef(null);

  const cachedJugador = jugadoresCache?.[routeIdJugador];
  const cachedPartidosTemporada =
    cachedJugador?.temporadas?.[temporadaActual]?.partidosRendimiento || cachedJugador?.partidosRendimiento;

  const [modalObservacionesOpen, setModalObservacionesOpen] = useState(false);
  const [modalInformesOpen, setModalInformesOpen] = useState(false);
  const [informesJugador, setInformesJugador] = useState(cachedJugador?.informesJugador || []);
  const [datosJugador, setDatosJugador] = useState(cachedJugador?.datosJugador);
  const [historicoJugador, setHistoricoJugador] = useState(cachedJugador?.historicoJugador);
  const [partidosRendimiento, setPartidosRendimiento] = useState(cachedPartidosTemporada);
  const [temporadaRendimiento, setTemporadaRendimiento] = useState(temporadaActual);
  const [loadingDatos, setLoadingDatos] = useState(!cachedJugador?.datosJugador);
  const [loadingHistorico, setLoadingHistorico] = useState(!cachedJugador?.historicoJugador);
  const [loadingHistorial, setLoadingHistorial] = useState(!cachedPartidosTemporada);
  const [loadingInformes, setLoadingInformes] = useState(!cachedJugador?.informesJugador);

  // Sincronizar estado inmediatamente durante render al cambiar de jugador en la URL
  const [prevRouteId, setPrevRouteId] = useState(routeIdJugador);
  if (prevRouteId !== routeIdJugador) {
    setPrevRouteId(routeIdJugador);
    const cached = jugadoresCache?.[routeIdJugador];
    const cachedTemp = cached?.temporadas?.[temporadaActual]?.partidosRendimiento || cached?.partidosRendimiento;
    if (cached?.datosJugador) {
      setDatosJugador(cached.datosJugador);
      setHistoricoJugador(cached.historicoJugador);
      setInformesJugador(cached.informesJugador || []);
      setPartidosRendimiento(cachedTemp || []);
      setTemporadaRendimiento(temporadaActual);
      setLoadingDatos(false);
      setLoadingHistorico(!cached.historicoJugador);
      setLoadingHistorial(!cachedTemp);
      setLoadingInformes(!cached.informesJugador);
    } else {
      setDatosJugador(undefined);
      setHistoricoJugador(undefined);
      setInformesJugador([]);
      setPartidosRendimiento(undefined);
      setTemporadaRendimiento(temporadaActual);
      setLoadingDatos(true);
      setLoadingHistorico(true);
      setLoadingHistorial(true);
      setLoadingInformes(true);
    }
  }

  const clubJugador = useMemo(() => {
    if (datosJugador?.isManual && datosJugador?.clubActual) {
      return capitalizar(datosJugador.clubActual);
    }
    if (historicoJugador && historicoJugador.length > 0) {
      const regActual = historicoJugador.find((h) => h.temporada === temporadaActual);
      const regUltimo = regActual || historicoJugador[0];
      if (regUltimo?.nombreClub) {
        return capitalizar(regUltimo.nombreClub);
      }
      if (regUltimo?.nombreEquipo) {
        return capitalizar(extraerNombreClub(regUltimo.nombreEquipo));
      }
    }
    return null;
  }, [datosJugador, historicoJugador, temporadaActual]);

  const numObservaciones = datosJugador?.observaciones?.length || 0;
  const numInformes = informesJugador?.length || 0;

  // Cálculo de la nota media de los informes del jugador
  const notaMediaInformes = useMemo(() => {
    if (!informesJugador || informesJugador.length === 0) {
      return null;
    }
    const conNota = informesJugador.filter(
      (inf) => inf.notaFinal !== null && inf.notaFinal !== undefined && inf.notaFinal !== ''
    );
    if (conNota.length === 0) {
      return null;
    }
    const suma = conNota.reduce((acc, inf) => acc + Number(inf.notaFinal), 0);
    return Number((suma / conNota.length).toFixed(1));
  }, [informesJugador]);

  const tieneNotaInformes = notaMediaInformes !== null;
  const estiloNotaInformes = obtenerColorNota(notaMediaInformes);

  const cargarDatos = async (tempConsultar = null) => {
    if (!routeIdJugador) {
      return;
    }
    const tempAConsultar = tempConsultar || temporadaRendimiento || temporadaActual;
    try {
      const [jugador, historico, historial, observaciones, informes] = await Promise.all([
        obtenerDatosJugador(routeIdJugador),
        obtenerHistoricoJugador(routeIdJugador),
        obtenerHistorialEvaluacionesJugador(routeIdJugador, tempAConsultar),
        obtenerObservacionesJugador(routeIdJugador).catch(() => []),
        obtenerInformesJugador(routeIdJugador).catch(() => []),
      ]);

      if (jugador) {
        jugador.observaciones = observaciones || [];
      }

      setDatosJugador(jugador);
      setHistoricoJugador(historico || []);
      setPartidosRendimiento(historial || []);
      setInformesJugador(informes || []);

      setJugadorCacheItem(routeIdJugador, {
        datosJugador: jugador,
        historicoJugador: historico || [],
        informesJugador: informes || [],
        temporadas: {
          [tempAConsultar]: {
            partidosRendimiento: historial || [],
          },
        },
      });
    } catch (err) {
      console.error('Error al recargar datos del jugador:', err);
    }
  };

  const handleCambiarTemporadaRendimiento = async (nuevaTemporada) => {
    setTemporadaRendimiento(nuevaTemporada);
    const cachedSeason = jugadoresCache?.[routeIdJugador]?.temporadas?.[nuevaTemporada]?.partidosRendimiento;
    if (cachedSeason) {
      setPartidosRendimiento(cachedSeason);
      setLoadingHistorial(false);
    } else {
      setLoadingHistorial(true);
    }

    try {
      const historial = await obtenerHistorialEvaluacionesJugador(routeIdJugador, nuevaTemporada);
      setPartidosRendimiento(historial || []);
      setJugadorCacheItem(routeIdJugador, {
        temporadas: {
          [nuevaTemporada]: {
            partidosRendimiento: historial || [],
          },
        },
      });
    } catch (err) {
      console.error('Error al cambiar temporada de rendimiento:', err);
    } finally {
      setLoadingHistorial(false);
    }
  };

  useEffect(() => {
    if (temporadaActual && !temporadaRendimiento) {
      setTemporadaRendimiento(temporadaActual);
    }
  }, [temporadaActual, temporadaRendimiento]);

  // Cargar datos desde la API con revalidación en segundo plano y caché SWR de forma independiente
  useEffect(() => {
    let cancelada = false;

    if (!routeIdJugador) {
      return;
    }

    const cached = jugadoresCache?.[routeIdJugador];
    const cachedTemp =
      cached?.temporadas?.[temporadaRendimiento || temporadaActual]?.partidosRendimiento || cached?.partidosRendimiento;

    if (!cached?.datosJugador) {
      setLoadingDatos(true);
    }
    if (!cached?.historicoJugador) {
      setLoadingHistorico(true);
    }
    if (!cachedTemp) {
      setLoadingHistorial(true);
    }
    if (!cached?.informesJugador) {
      setLoadingInformes(true);
    }

    // 1. Datos del jugador y observaciones
    Promise.all([obtenerDatosJugador(routeIdJugador), obtenerObservacionesJugador(routeIdJugador).catch(() => [])])
      .then(([jugador, observaciones]) => {
        if (cancelada || !jugador) {
          if (!cancelada) {
            setLoadingDatos(false);
          }
          return;
        }
        jugador.observaciones = observaciones || [];
        setDatosJugador(jugador);
        setJugadorCacheItem(routeIdJugador, { datosJugador: jugador });
        setLoadingDatos(false);
      })
      .catch((err) => {
        console.error('Error al cargar datos del jugador:', err);
        if (!cancelada) {
          setLoadingDatos(false);
        }
      });

    // 2. Histórico de temporadas del jugador
    obtenerHistoricoJugador(routeIdJugador)
      .then((historico) => {
        if (cancelada) {
          return;
        }
        setHistoricoJugador(historico || []);
        setJugadorCacheItem(routeIdJugador, { historicoJugador: historico || [] });
        setLoadingHistorico(false);
      })
      .catch((err) => {
        console.error('Error al cargar histórico del jugador:', err);
        if (!cancelada) {
          setLoadingHistorico(false);
        }
      });

    // 3. Partidos de rendimiento / temporada
    const tempAConsultar = temporadaRendimiento || temporadaActual;
    obtenerHistorialEvaluacionesJugador(routeIdJugador, tempAConsultar)
      .then((historial) => {
        if (cancelada) {
          return;
        }
        setPartidosRendimiento(historial || []);
        setJugadorCacheItem(routeIdJugador, {
          temporadas: {
            [tempAConsultar]: {
              partidosRendimiento: historial || [],
            },
          },
        });
        setLoadingHistorial(false);
      })
      .catch((err) => {
        console.error('Error al cargar historial de rendimiento:', err);
        if (!cancelada) {
          setLoadingHistorial(false);
        }
      });

    // 4. Informes de scouting del jugador
    obtenerInformesJugador(routeIdJugador)
      .then((informes) => {
        if (cancelada) {
          return;
        }
        setInformesJugador(informes || []);
        setJugadorCacheItem(routeIdJugador, { informesJugador: informes || [] });
        setLoadingInformes(false);
      })
      .catch((err) => {
        console.error('Error al cargar informes del jugador:', err);
        if (!cancelada) {
          setLoadingInformes(false);
        }
      });

    return () => {
      cancelada = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeIdJugador, temporadaActual]);

  const handleDelete = async () => {
    if (!datosJugador) {
      return;
    }
    if (
      window.confirm(
        `¿Estás seguro de que quieres eliminar a ${capitalizar(datosJugador.nombre)}? Esta acción no se puede deshacer.`
      )
    ) {
      const eliminado = await eliminarJugador(datosJugador.idJugador);
      if (eliminado) {
        navigate('/home');
      } else {
        alert('Error al eliminar el jugador');
      }
    }
  };

  const estiloSeparador = {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.3), transparent)',
    mb: 2,
  };

  return (
    <ContainerApp maxWidth="xl" sx={{ mt: 3.5 }}>
      {/* Título: Nombre del jugador y Club */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          minHeight: 48,
        }}
      >
        {loadingDatos && !datosJugador ? (
          <CircularProgress size={24} sx={{ color: theme.palette.primary.main, my: 1 }} />
        ) : (
          <>
            <PageHeaderBox sx={{ m: 0, p: 0, lineHeight: 1.3 }}>
              {capitalizar(datosJugador?.nombre || '')}
            </PageHeaderBox>
            {clubJugador && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 1,
                  px: 1.5,
                  py: 0.4,
                  borderRadius: '9999px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                  fontSize: '0.84rem',
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                }}
              >
                <SecurityIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                <span>{clubJugador}</span>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Separador horizontal */}
      <Box sx={estiloSeparador} />

      {/* Acciones y metadatos */}
      {datosJugador && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: 1.5,
            mb: 2,
          }}
        >
          {/* Botones de acción compactos */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BotonListasConJugador
              idJugador={datosJugador.idJugador}
              enLista={false}
              badge
              sx={{
                height: 32,
                px: 1.5,
                borderRadius: '6px',
                fontSize: '0.80rem',
                fontWeight: 600,
              }}
            />

            <Tooltip title={numObservaciones > 0 ? `${numObservaciones} observación(es)` : 'Añadir observación'}>
              <Box
                onClick={() => setModalObservacionesOpen(true)}
                sx={{
                  height: 32,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  borderRadius: '6px',
                  fontSize: '0.80rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.15s ease-in-out',
                  backgroundColor: numObservaciones > 0 ? '#e0f2fe' : '#ffffff',
                  color: numObservaciones > 0 ? '#0369a1' : '#334155',
                  border: `1px solid ${numObservaciones > 0 ? '#7dd3fc' : '#cbd5e1'}`,
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: numObservaciones > 0 ? '#bae6fd' : '#f8fafc',
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-0.5px)',
                  },
                }}
              >
                <ModeComment sx={{ fontSize: 15 }} />
                <span>Observación</span>
                {numObservaciones > 0 && (
                  <Box
                    sx={{
                      ml: 0.25,
                      px: 0.7,
                      py: 0.1,
                      borderRadius: '10px',
                      fontSize: '0.70rem',
                      fontWeight: 700,
                      backgroundColor: '#0284c7',
                      color: '#ffffff',
                    }}
                  >
                    {numObservaciones}
                  </Box>
                )}
              </Box>
            </Tooltip>

            {/* Botón para Informes Detallados de Scouting */}
            <Tooltip
              title={
                numInformes > 0
                  ? tieneNotaInformes
                    ? `${numInformes} informe(s) · Nota ${numInformes > 1 ? 'media' : 'final'}: ${notaMediaInformes.toFixed(1)}`
                    : `${numInformes} informe(s)`
                  : 'Añadir informe'
              }
            >
              <Box
                onClick={() => setModalInformesOpen(true)}
                sx={{
                  height: 32,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  borderRadius: '6px',
                  fontSize: '0.80rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.15s ease-in-out',
                  backgroundColor: tieneNotaInformes ? estiloNotaInformes.bg : numInformes > 0 ? '#ecfdf5' : '#ffffff',
                  color: tieneNotaInformes ? estiloNotaInformes.color : numInformes > 0 ? '#065f46' : '#334155',
                  border: `1px solid ${
                    tieneNotaInformes ? estiloNotaInformes.border : numInformes > 0 ? '#a7f3d0' : '#cbd5e1'
                  }`,
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    filter: 'brightness(0.96)',
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-0.5px)',
                  },
                }}
              >
                <AssessmentIcon sx={{ fontSize: 16 }} />
                <span>Informe</span>
                {loadingInformes && (!informesJugador || informesJugador.length === 0) ? (
                  <CircularProgress size={12} sx={{ ml: 0.25, color: 'inherit' }} />
                ) : tieneNotaInformes ? (
                  <Box
                    sx={{
                      ml: 0.25,
                      px: 0.7,
                      py: 0.1,
                      borderRadius: '10px',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      backgroundColor: estiloNotaInformes.dark || estiloNotaInformes.color,
                      color: '#ffffff',
                      lineHeight: 1.2,
                    }}
                  >
                    {notaMediaInformes.toFixed(1)}
                  </Box>
                ) : numInformes > 0 ? (
                  <Box
                    sx={{
                      ml: 0.25,
                      px: 0.7,
                      py: 0.1,
                      borderRadius: '10px',
                      fontSize: '0.70rem',
                      fontWeight: 700,
                      backgroundColor: '#059669',
                      color: '#ffffff',
                    }}
                  >
                    {numInformes}
                  </Box>
                ) : null}
              </Box>
            </Tooltip>
          </Box>

          {/* Separador vertical */}
          <Box sx={{ borderLeft: '1px solid #cbd5e1', height: 24, mx: 1.25, display: { xs: 'none', md: 'block' } }} />

          {/* Metadatos */}
          <DatosJugador
            key={`${datosJugador.ano}_${datosJugador.demarcacion}_${datosJugador.lateralidad}`}
            datosJugador={datosJugador}
            temporadaActual={temporadaActual}
            onDatosChange={(cambios) =>
              setDatosJugador((prev) => {
                const updated = { ...prev, ...cambios };
                setJugadorCacheItem(routeIdJugador, { datosJugador: updated });
                return updated;
              })
            }
          />
        </Box>
      )}

      {/* Modales de Edición, Observaciones e Informes */}
      {datosJugador && (
        <>
          <CrearJugadorModal ref={editModalRef} onUpdate={cargarDatos} />
          <ModalObservacionesJugador
            open={modalObservacionesOpen}
            onClose={() => setModalObservacionesOpen(false)}
            idJugador={datosJugador.idJugador}
            nombreJugador={datosJugador.nombre}
            observaciones={datosJugador.observaciones || []}
            onUpdate={(updated) => {
              if (updated) {
                setDatosJugador((prev) => {
                  const nextJugador = { ...prev, observaciones: updated };
                  setJugadorCacheItem(routeIdJugador, { datosJugador: nextJugador });
                  return nextJugador;
                });
              } else {
                cargarDatos();
              }
            }}
          />
          <ModalInformesJugador
            open={modalInformesOpen}
            onClose={() => setModalInformesOpen(false)}
            idJugador={datosJugador.idJugador}
            nombreJugador={datosJugador.nombre}
            informes={informesJugador}
            onUpdate={(updated) => {
              if (updated) {
                setInformesJugador(updated);
                setJugadorCacheItem(routeIdJugador, { informesJugador: updated });
              } else {
                cargarDatos();
              }
            }}
          />
        </>
      )}

      {/* Separador horizontal */}
      {datosJugador && <Box sx={{ ...estiloSeparador, mb: 2.5 }} />}

      <Grid container spacing={2.5} justifyContent="center">
        {datosJugador?.isManual ? (
          <InfoJugadorManual onEdit={() => editModalRef.current?.handleOpen(datosJugador)} onDelete={handleDelete} />
        ) : (
          <>
            <Grid item xs={12} md={11}>
              <TablaHistorialRendimiento
                partidos={partidosRendimiento}
                temporada={temporadaRendimiento || temporadaActual}
                onTemporadaChange={handleCambiarTemporadaRendimiento}
                loading={loadingHistorial}
                historicoJugador={historicoJugador}
                embedded
              />
            </Grid>
            <Grid item xs={12} md={11}>
              <HistoricoJugador
                historicoJugador={historicoJugador}
                temporadaActual={temporadaActual}
                loading={loadingHistorico}
              />
            </Grid>
          </>
        )}
      </Grid>
    </ContainerApp>
  );
};

export default Jugador;
